import { useFormik } from 'formik';
import Banner from 'src/components/atoms/Banner';
import { AgButton, AgCheckbox, AgInput, AgModal, AgModalActions, AgModalContent, AgSelect } from 'src/components/ui';
import { useCreateAccount, useUpdateAccount } from 'src/modules/accounting/useAccountMutations';
import { LedgerAccountDetails, useLedgerAccounts } from 'src/modules/accounting/useLedgerAccounts';
import { LedgerAccountType } from 'types/graphql';
import { z } from 'zod';

const ACCOUNT_TYPES: { value: LedgerAccountType; label: string; category: string }[] = [
  { value: 'CashAssets', label: 'Cash Assets', category: 'Assets' },
  { value: 'CurrentAssets', label: 'Current Assets', category: 'Assets' },
  { value: 'LongTermAssets', label: 'Long-Term Assets', category: 'Assets' },
  { value: 'OtherAssets', label: 'Other Assets', category: 'Assets' },
  { value: 'CurrentLiabilities', label: 'Current Liabilities', category: 'Liabilities' },
  { value: 'LongTermLiabilities', label: 'Long-Term Liabilities', category: 'Liabilities' },
  { value: 'Equity', label: 'Equity', category: 'Equity' },
  { value: 'Income', label: 'Income', category: 'Income' },
  { value: 'OtherIncome', label: 'Other Income', category: 'Income' },
  { value: 'DirectCosts', label: 'Cost of Goods Sold', category: 'Expenses' },
  { value: 'IndirectCosts', label: 'Operating Expenses', category: 'Expenses' },
  { value: 'OtherCosts', label: 'Other Expenses', category: 'Expenses' },
];

const accountSchema = z.object({
  accountNumber: z
    .string()
    .min(1, 'Account number is required')
    .regex(/^\d+(\.\d+)?$/, 'Account number must be a valid number (e.g., 200, 510.50)'),
  accountName: z
    .string()
    .min(1, 'Account name is required')
    .regex(
      /^[a-zA-Z0-9\s\-/()]+$/,
      'Account name can only contain letters, numbers, spaces, hyphens, slashes, and parentheses'
    ),
  accountType: z.string().min(1, 'Account type is required'),
  parentId: z.number().nullable(),
  isPhysical: z.boolean(),
});

type AccountFormValues = z.infer<typeof accountSchema>;

interface AccountDialogProps {
  open: boolean;
  onClose: () => void;
  account?: Partial<LedgerAccountDetails>;
}

export default function AccountDialog({ open, onClose, account }: AccountDialogProps) {
  const isEdit = !!account?.id;
  const accounts = useLedgerAccounts();

  const { createAccount, loading: creating } = useCreateAccount();
  const { updateAccount, loading: updating } = useUpdateAccount();

  const f = useFormik<AccountFormValues>({
    enableReinitialize: true,
    initialValues: {
      accountName: account?.name || '',
      accountNumber: account?.number || '',
      accountType: account?.accountType || '',
      parentId: account?.parentId ?? null,
      isPhysical: account?.isPhysical || false,
    },
    validate: (values) => {
      const result = accountSchema.safeParse(values);
      if (result.success) return {};

      const errors: Partial<Record<keyof AccountFormValues, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof AccountFormValues;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      return errors;
    },
    onSubmit: async (data) => {
      const input = {
        accountName: data.accountName,
        accountNumber: data.accountNumber,
        accountType: data.accountType as LedgerAccountType,
        parentId: data.parentId,
        isPhysical: data.isPhysical,
      };

      // The mutation hooks have no onError, so a failed save rejects. The shared Apollo error link already toasts it;
      // here a failure only keeps the dialog open.
      const result = await (isEdit
        ? updateAccount({ variables: { id: account.id!, input } })
        : createAccount({ variables: { input } })
      ).catch(() => null);

      if (!result || result.errors?.length || !result.data) return;

      f.resetForm();
      onClose();
    },
  });

  const selectedCategory = ACCOUNT_TYPES.find((t) => t.value === f.values.accountType)?.category;

  const availableParents = accounts.filter((acc) => {
    if (!selectedCategory) return false;
    const accCategory = ACCOUNT_TYPES.find((t) => t.value === acc.accountType)?.category;
    return accCategory === selectedCategory && acc.id !== account?.id;
  });

  return (
    <AgModal open={open} onClose={onClose} title={isEdit ? 'Edit Account' : 'Add Account'} size="sm">
      <form onSubmit={f.handleSubmit}>
        <AgModalContent>
          <div className="flex flex-col gap-6">
            {!isEdit && (
              <Banner
                variant="info"
                title="Accounts cannot be deleted once created. You can disable them instead if needed."
              />
            )}

            <AgInput
              name="accountNumber"
              label="Account Number"
              placeholder="900.00"
              isRequired
              value={f.values.accountNumber}
              onChange={(val) => f.setFieldValue('accountNumber', val)}
              errorMessage={f.touched.accountNumber ? f.errors.accountNumber : undefined}
            />

            <AgInput
              name="accountName"
              label="Account Name"
              placeholder="My Account"
              isRequired
              value={f.values.accountName}
              onChange={(val) => f.setFieldValue('accountName', val)}
              errorMessage={f.touched.accountName ? f.errors.accountName : undefined}
            />

            <AgSelect
              label="Account Type"
              isRequired
              options={ACCOUNT_TYPES.map((type) => ({ value: type.value, label: `${type.label} (${type.category})` }))}
              value={f.values.accountType || null}
              onChange={(key) => {
                f.setFieldValue('accountType', key);
                f.setFieldValue('parentId', null);
              }}
            />

            {/* Parent Account */}
            <AgSelect
              label="Parent Account (Optional)"
              isDisabled={!f.values.accountType}
              options={[
                { value: '', label: 'None (Root Level)' },
                ...availableParents.map((acc) => ({ value: String(acc.id), label: `${acc.number} ${acc.name}` })),
              ]}
              value={f.values.parentId != null ? String(f.values.parentId) : ''}
              onChange={(key) => f.setFieldValue('parentId', key === '' ? null : Number(key))}
            />

            <AgCheckbox
              isSelected={f.values.isPhysical}
              onChange={(val) => f.setFieldValue('isPhysical', val)}
              label="Physical Account (allows ledger entries to be posted directly to this account)"
            />
          </div>
        </AgModalContent>

        <AgModalActions>
          <AgButton type="submit" variant="primary" isDisabled={creating || updating}>
            {creating || updating ? 'Saving...' : isEdit ? 'Update Account' : 'Create Account'}
          </AgButton>
        </AgModalActions>
      </form>
    </AgModal>
  );
}
